import { getCookie } from '@avalara/bat-apps-shared/helpers/cookies';
import sessionStorage from '@avalara/bat-apps-shared/helpers/sessionStorage';
import { t } from '@avalara/bat-apps-shared/i18n';
import type { Dispatch, ThunkAction } from '@avalara/types/actions';
import type { Result } from '@avalara/types/index';

import type { InitialAddObligationsOptions } from '@avalara/types/index';
import type { State } from '@avalara/types/reducer-states';
import type { RouteComponentProps } from 'react-router-dom';
import type { AddObligationResponse, CreateOfferRequest, ReturnsBuyRequest } from '../../../../types';
import {
  type ClientOfferWithPlans,
  type DiscountConfig,
  type OfferFlow,
  type OfferPurchaseRequest,
  type OfferSpecificQueryParams,
  Pages,
} from '../../../../types/offers';
import { formatPrice } from '../../../shared/helpers';
import conf from '../../conf';
import {
  getAnalyticsByActiveFlow,
  getOfferByActiveFlow,
  getOfferMetadataByActiveFlow,
} from '../../reducers/aggregate-logic';
import { getOrderTotal, getSelectedPlan, getValidatedConnectorId } from '../../selectors/order';
import { selectReturnsPricingData } from '../../selectors/returns';
import { setBuySuccess } from '../../store/avatax-signup';
import { setOffer } from '../../store/offer';
import { setDiscountPercent, setOfferSpecificSettings } from '../../store/order';
import { setFetchStatus } from '../../store/system';
import analytics from '../analytics';
import { getTotalCost } from '../buy-flow';
import fetch from '../fetch';
import { embeddedViewRequested, getWindowQueryUnlessInProd } from '../helpers';
import localStorage from '../local-storage';
import { createAccountWithOffer } from './accounts';
import { trackAvataxPurchase, trackOfferPurchaseAnalytics, updateProductAnalytics } from './analytics';
import { getSystemErrorMessaging } from './error';
import { authRedirect, basePostParams } from './index';
import { goToNextOfferStep } from './offer-flow';

type AddOfferParams = {
  dispatch: Dispatch;
  state: State;
  paymentToken: string;
  path: string;
};

const addOfferToAccount = async (params: AddOfferParams): Promise<Result<AddObligationResponse>> => {
  const { dispatch, state, paymentToken, path } = params;
  const { account, data } = state;
  if (account.accountId == null)
    return Promise.resolve({ ok: false, status: 400, message: t('client.errors.no-account-id') });

  const postBody: CreateOfferRequest = {
    accountId: account.accountId,
    offer: getOfferByActiveFlow(state, paymentToken),
    marketingData: data.marketingData,
    info: getOfferMetadataByActiveFlow(state),
  };

  const postParams = {
    ...basePostParams,
    body: JSON.stringify(postBody),
  };

  return fetch(dispatch)(`${conf.API_URL}/api/accounts${path}`, postParams);
};

export const addPaidAvataxTier =
  ({ paymentToken, history }: InitialAddObligationsOptions): ThunkAction =>
  async (dispatch: Dispatch, getState: () => State): Promise<void> => {
    const { account, buyFlow } = getState();

    if (getCookie('TESTCAFE_E2E')) {
      history.push('/buy/success');
      return;
    }

    const result: Result<AddObligationResponse> = await addOfferToAccount({
      dispatch,
      state: getState(),
      paymentToken,
      path: '/offers/avatax',
    });

    if (!result.ok) {
      dispatch(
        getSystemErrorMessaging({
          errorCode: 'signupError',
          msg: 'We were unable to set up your AvaTax tier.',
          serverMsg: result.message,
          isPaid: true,
        }),
      );
      return;
    }

    // If the user is no longer logged in, send them to AI to do so
    if (result.value.unauthenticatedUser === true) {
      authRedirect();
      return;
    }

    const { eventName, data } = getAnalyticsByActiveFlow(getState());
    analytics.track(eventName, data);

    if (account.accountId == null) return;
    const { accountId } = account;
    localStorage.set(`paidAvataxOrBundlePurchase-${accountId}`, 'true');

    trackAvataxPurchase({
      selectedTier: buyFlow.selectedTier,
      connector: account.connector,
      price: getTotalCost(buyFlow),
    });

    history.push('/buy/success');
    dispatch(setBuySuccess());
  };

export const addReturnsOffer =
  ({ history, paymentToken: paymentMethodId, handleError = () => {} }: InitialAddObligationsOptions) =>
  async (dispatch: Dispatch, getState: () => State) => {
    const state = getState();
    const {
      account: { accountId },
      data: { marketingData },
      order: { discountName },
      system: { activeFlow },
    } = state;

    if (getCookie('TESTCAFE_E2E')) {
      history.push('/returns/thank-you');
      return;
    }

    if (accountId == null) return;

    const { totalPrice } = selectReturnsPricingData(state);

    const postBody: ReturnsBuyRequest = {
      accountId,
      activeFlow,
      discountName,
      marketingData,
      paymentMethodId,
      setupReturnsLink: `${conf.CUP_URL}/cup/a/${accountId}/returns-provisioning`,
      totalPrice,
    };

    const postParams = {
      ...basePostParams,
      body: JSON.stringify(postBody),
    };

    const query = getWindowQueryUnlessInProd();
    const result = await fetch(dispatch)<AddObligationResponse>(
      `${conf.API_URL}/api/accounts/offers/returns${query}`,
      postParams,
    );

    if (!result.ok || result.value.unauthenticatedUser === true) {
      dispatch(
        getSystemErrorMessaging({
          errorCode: 'signupError',
          msg: 'We were unable to set up your returns subscription.',
          serverMsg: 'message' in result && typeof result.message === 'string' ? result.message : '',
          isPaid: true,
        }),
      );
      handleError();
      return;
    }

    const { eventName, data } = getAnalyticsByActiveFlow(getState());
    analytics.track(eventName, data);

    dispatch(updateProductAnalytics('purchase'));

    history.push('/returns/thank-you');
  };

export const purchaseOffer =
  (history: RouteComponentProps['history'], paymentMethodId?: string) =>
  async (dispatch: Dispatch, getState: () => State) => {
    const state = getState();
    const {
      account: { accountId, isLoggedIn },
      data: { marketingData },
      offer: { offerFlow, offerName, pages },
      order: { discountCode, offerSpecificSettings, quantity, selectedPlanName, salesTax },
      system: { inProgressFetches },
    } = state;

    if (!isLoggedIn) {
      dispatch(createAccountWithOffer(history, paymentMethodId));
      return;
    }

    const hasPurchasedOffer: boolean = sessionStorage.get('hasPurchasedOffer') === 'true';

    if (accountId == null || !offerFlow) return;
    if (inProgressFetches.purchaseOffer || hasPurchasedOffer) return;

    dispatch(setFetchStatus({ fetchName: 'purchaseOffer', inProgress: true }));

    const selectedPlan = getSelectedPlan(state);
    if (!selectedPlanName || !selectedPlan) return;
    const currency = selectedPlan.currency || undefined;

    const postBody: OfferPurchaseRequest = {
      marketingData,
      discountCode,
      offerFlow,
      offerSpecificSettings,
      quantity,
      totalCost: formatPrice(getOrderTotal(getState()), 2, currency),
      paymentMethodId,
      selectedPlanName,
      connectorId: getValidatedConnectorId(state),
      salesTax,
    };

    const postParams = {
      ...basePostParams,
      body: JSON.stringify(postBody),
    };

    const searchParams = new URLSearchParams(window.location.search);

    const result: Result<AddObligationResponse> = await fetch(dispatch)(
      `${conf.API_URL}/api/accounts/offers?${searchParams}`,
      postParams,
    );

    dispatch(setFetchStatus({ fetchName: 'purchaseOffer', inProgress: false }));

    if (!result.ok || result.value.unauthenticatedUser) {
      analytics.track('ADD_OFFER_TO_EXISTING_ACCOUNT_FAILURE', {
        accountId,
        offerId: offerFlow,
      });

      dispatch(
        getSystemErrorMessaging({
          errorCode: 'signupError',
          msg: `We couldn’t finish adding ${offerName} to your account.`,
          serverMsg: 'message' in result && typeof result.message === 'string' ? result.message : '',
          isPaid: true,
        }),
      );

      if (embeddedViewRequested()) {
        window.parent.postMessage({ event: 'add-offer', success: false, type: offerFlow }, '*');
      }

      return;
    }

    if (embeddedViewRequested() && !pages.includes(Pages.SettingUp)) {
      window.parent.postMessage({ event: 'add-offer', success: true, type: offerFlow }, '*');
    }

    sessionStorage.set('hasPurchasedOffer', 'true');

    dispatch(trackOfferPurchaseAnalytics(offerFlow, accountId, false));
    dispatch(goToNextOfferStep({ history }));
  };

export const validateDiscountCode =
  (flow: OfferFlow | string, discountCode: string) =>
  async (dispatch: Dispatch): Promise<void> => {
    if (!flow) return;

    const result = await fetch(dispatch)<DiscountConfig>(
      `${conf.API_URL}/api/accounts/discount/${flow}/${discountCode}`,
    );
    if (!result.ok) return;

    dispatch(setDiscountPercent(result.value));
  };

export const addRiskAssessmentOffer =
  ({ history, paymentToken: paymentMethodId, handleError = () => {} }: InitialAddObligationsOptions) =>
  async (dispatch: Dispatch, getState: () => State) => {
    const {
      account: { accountId },
      data: { marketingData },
      system: { activeFlow },
      order: { discountCode },
    } = getState();

    if (accountId == null) return;

    const postBody: CreateOfferRequest = {
      accountId,
      activeFlow,
      marketingData,
      offer: getOfferByActiveFlow(getState(), paymentMethodId),
      paymentMethodId,
      discountCode,
    };

    const postParams = {
      ...basePostParams,
      body: JSON.stringify(postBody),
    };

    const result: Result<AddObligationResponse> = await fetch(dispatch)(
      `${conf.API_URL}/api/accounts/offers/risk-assessment`,
      postParams,
    );

    if (!result.ok || result.value.unauthenticatedUser === true) {
      dispatch(
        getSystemErrorMessaging({
          errorCode: 'signupError',
          msg: 'We were unable to set up your Sales Tax Risk Assessment.',
          serverMsg: 'message' in result && typeof result.message === 'string' ? result.message : '',
          isPaid: true,
        }),
      );
      // TODO: refactor out this handleError function in light of above error handler
      handleError();
      return;
    }

    const { eventName, data } = getAnalyticsByActiveFlow(getState());
    analytics.track(eventName, data);

    dispatch(updateProductAnalytics('purchase'));

    history.push('/risk-assessment/thank-you');
  };

const getOfferSpecificQueries =
  (offerSpecificQueryParams: OfferSpecificQueryParams = {}) =>
  (dispatch: Dispatch) => {
    const searchParams = new URLSearchParams(window.location.search);

    const offerSpecificQueries = Object.keys(offerSpecificQueryParams).reduce(
      (queries, key) => {
        queries[key] = searchParams.get(offerSpecificQueryParams[key]);
        return queries;
      },
      {} as Record<string, string | null>,
    );

    dispatch(setOfferSpecificSettings(offerSpecificQueries));
  };

export const fetchOffer = (offerFlow: string) => async (dispatch: Dispatch) => {
  const result: Result<ClientOfferWithPlans> = await fetch(dispatch)(
    `${conf.API_URL}/api/offers/${offerFlow}${window.location.search}`,
  );

  if (result.ok) {
    const clientDetails = result.value;
    dispatch(getOfferSpecificQueries(clientDetails.offerSpecificQueryParams));
    dispatch(setOffer(clientDetails));
  } else {
    dispatch(setOffer(null));
    if (result.status !== 404) {
      dispatch(getSystemErrorMessaging({ errorCode: 'genericError' }));
    }
  }
};
